<template>
  <div class="page-grid">
    <div class="col-span-12 md:col-span-7 xl:col-span-6 xl:col-start-2">
      <CheckoutForm
        :cart="cart"
        :user="user"
        @cart-updated="handleCartUpdated"
        @registration-updated="handleRegistrationUpdated"
      />
    </div>

    <div class="col-span-12 flex flex-col gap-y-6 md:col-span-5 xl:col-span-4">
      <OrderSummary 
        :cart="cart"
        :store-credit-applied="storeCreditIsApplied"
        :store-credit-amount="storeCreditUsedAmount"
      />

      <BaseCard v-if="!isConfirmed && Boolean(cart?.prices.grand_total.value) && storeCreditAvailableAmount > 0" title="Store Credit">
        <p class="body-md">
          You have ${{ storeCreditAvailableAmount }} available in store credit.
        </p>

        <div class="flex flex-col gap-y-2">
          <div class="flex items-start gap-x-3">
            <input 
              id="applyStoreCredit"
              type="checkbox" 
              :checked="storeCreditIsApplied"
              @change="applyStoreCreditHandler"
            />
            <label
              for="applyStoreCredit"
              class="font-normal mb-0"
            >
              <span>Apply Store Credit</span>
            </label>
          </div>
        </div>
      </BaseCard>

      <CouponCard
        v-if="!isConfirmed"
        :cart="cart"
        :registration-id="registrationId"
        @cart-updated="handleCartUpdated"
      />

      <BaseCard title="Cancellation/Change Policy">
        <p class="body-md">
          Registration consents to the <a
            :href="utilityLinks.CANCELLATION_POLICY"
            class="link"
            rel="noopener noreferrer"
            target="_blank"
          >Cancellation and Change Policy</a>
        </p>
      </BaseCard>

      <BaseCard title="Questions?">
        <p class="body-md">
          Call the Admissions Team: <a
            :href="`tel:${utilityLinks.ADMISSIONS_TEAM_NUMBER}`"
            class="link"
            v-text="utilityLinks.ADMISSIONS_TEAM_NUMBER"
          />
        </p>
      </BaseCard>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onBeforeMount, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { utilityLinks } from '@js/utils/links'
import { Cart } from '@js/api/types/cart/Cart'
import { getStoreCredit } from '@js/api/checkout/getStoreCredit'
import { applyStoreCredit } from '@js/api/checkout/applyStoreCredit'
import { removeStoreCredit } from '@js/api/checkout/removeStoreCredit'

import BaseCard from '@js/components/global/BaseCard.vue'
import CheckoutForm from '@js/components/checkout/partials/CheckoutForm.vue'
import OrderSummary from '@js/components/checkout/partials/OrderSummary.vue'
import CouponCard from '@js/components/checkout/partials/CouponCard.vue'
import useAuth from '@js/composables/useAuth'
import useCart from '@js/composables/useCart'

defineOptions({
  name: 'AppCheckout',
})

const { getAuthUser, getLastRegistration } = useAuth()
const { getCartFromMagento, getCartFromStorage } = useCart()

const route = useRoute()
const isConfirmed = computed(() => route.query.confirmed === 'true')

const cart = ref<Cart | null>(null)
const registrationId = ref<number | null>(null)

const user = computed(() => getAuthUser())

const storeCreditAvailableAmount = ref(0)
const storeCreditIsApplied = ref(false)
const storeCreditUsedAmount = ref(0)

const applyStoreCreditHandler = async (event: Event) => {
  if (!cart.value?.id) return

  const isChecked = (event.target as HTMLInputElement).checked

  let updatedCartData

  if (isChecked) {
    updatedCartData = await applyStoreCredit(cart.value.id)
  } else {
    updatedCartData = await removeStoreCredit(cart.value.id)
  }

  if (updatedCartData) {
    storeCreditIsApplied.value = isChecked
    cart.value = {
      ...cart.value,
      prices: {
        ...cart.value.prices,
        ...updatedCartData.prices,
      },
      applied_store_credit: updatedCartData.applied_store_credit,
    }

    const applied = updatedCartData.applied_store_credit?.applied_balance.value ?? 0

    storeCreditUsedAmount.value = applied
    storeCreditIsApplied.value = applied > 0
  }
}

const handleCartUpdated = (updatedCart: Cart | null) => {
  if (!updatedCart) return

  cart.value = updatedCart
}

const handleRegistrationUpdated = (id: number) => {
  registrationId.value = id
}

onBeforeMount(async () => {
  await getCartFromMagento()

  cart.value = getCartFromStorage()

  if (!cart.value || cart.value.items.length === 0) {
    window.location.assign(utilityLinks.COURSES_PAGE)
  }

  registrationId.value = getLastRegistration()

  if (cart.value?.applied_store_credit?.applied_balance.value) {
    storeCreditIsApplied.value = true
    storeCreditUsedAmount.value = cart.value.applied_store_credit.applied_balance.value
  }

  storeCreditAvailableAmount.value = await getStoreCredit()
})
</script>